import React, { useEffect, useState } from 'react';
import reviewsApi from '../../services/reviewsApi';

const ReviewDisplay = ({ reviewId, onClose }) => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    
    reviewsApi.getReview(reviewId)
      .then((data) => {
        if (cancelled) return;
        setReview(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Unable to load review.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
      
    return () => { cancelled = true; };
  }, [reviewId]);

  if (loading) {
    return (
      <div className="review-display-loading">
        Loading review…
      </div>
    );
  }

  if (error) {
    return (
      <div className="review-display-error">
        <p>{error}</p>
        {onClose && (
          <button onClick={onClose} className="button">
            Close
          </button>
        )}
      </div>
    );
  }

  if (!review) {
    return (
      <div className="review-display-empty">
        <p>Review not found.</p>
        {onClose && (
          <button onClick={onClose} className="button">
            Close
          </button>
        )}
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const renderRatingStars = (rating, maxRating = 5) => {
    const stars = [];
    for (let i = 1; i <= maxRating; i++) {
      stars.push(
        <span key={i} className={`star ${i <= rating ? 'filled' : 'empty'}`}>
          ★
        </span>
      );
    }
    return <div className="rating-stars">{stars}</div>;
  };

  const humanizeBadgeKey = (key) => {
    return String(key).replace(/_/g, ' ').replace(/(^|\s)\S/g, (t) => t.toUpperCase());
  };

  return (
    <div className="review-display">
      <div className="review-header">
        <h3>Review</h3>
        <div className="review-meta">
          <span className="review-author">By: {review.author_name}</span>
          <span className="review-date">Date: {formatDate(review.created_at)}</span>
        </div>
      </div>

      <div className="review-content">
        {review.fields && !Array.isArray(review.fields) && typeof review.fields === 'object' ? (
          Object.entries(review.fields).map(([key, value]) => (
            <div key={key} className="review-field">
              <label>{key.replace(/_/g, ' ').replace(/(^|\s)\S/g, (t) => t.toUpperCase())}:</label>
              <div className="review-field-value">
                {typeof value === 'number' ? (
                  <div className="rating-display">
                    {renderRatingStars(parseInt(value))}
                    <span className="rating-text">{value} / 5</span>
                  </div>
                ) : (
                  <div className="text-content">
                    {value || 'No comment provided'}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          // If fields is already an array, keep existing behavior
          Array.isArray(review.fields) && review.fields.map((field) => (
            <div key={field.key} className="review-field">
              <label>{field.label}:</label>
              <div className="review-field-value">
                {field.type === 'rating' ? (
                  <div className="rating-display">
                    {renderRatingStars(parseInt(field.value))}
                    <span className="rating-text">{field.value} / {field.max || 5}</span>
                  </div>
                ) : (
                  <div className="text-content">
                    {field.value || 'No comment provided'}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {Array.isArray(review.badge_keys) && review.badge_keys.length > 0 && (
        <div className="review-badges-display">
          <span className="review-badges-display-label">Badges awarded:</span>
          <div className="review-badges-display-list">
            {review.badge_keys.map((key) => (
              <span key={key} className="review-badge-chip">
                {humanizeBadgeKey(key)}
              </span>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(review.responses) && review.responses.length > 0 && (
        (() => {
          const resp = review.responses[0];
          return (
            <div className="review-response">
              <h4>Response</h4>
              <div className="response-content">
                <p>{resp.content}</p>
                <div className="response-meta">
                  <span>By: {resp.author_name}</span>
                  <span>Date: {formatDate(resp.created_at)}</span>
                </div>
              </div>
            </div>
          );
        })()
      )}

      <div className="review-actions">
        {onClose && (
          <button onClick={onClose} className="button">
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default ReviewDisplay;
